import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Box } from "@mui/material";
import { logger } from "@platform/browser-logger";
import {
  BTX_CH_BENEFITS_FIELD,
  BTX_CURRENT_CARRIER_POLICY_DROPDOWN,
} from "@platform/feature-flags";
import { DialogProvider, NotificationProvider } from "@platform/hooks";
import { CoverageTypeEnum } from "@platform/types";
import { forwardRef, useCallback, useMemo, useRef } from "react";
import {
  DialogName,
  DuplicateBenefitPlanGroupArgs,
  EditorValuesFilterProvider,
  PlanDesignerConfiguration,
  RfpCoverage,
  ValidationProvider,
  getLastUpdatedLabel,
  normalizeNoteBenefitPlanType,
  stripNotOffered,
  useRequiredFieldsValidation,
} from "@platform/plan-grid-core";
import { PlanEditor } from "@platform/plan-grid-core";
import {
  FieldMetadataProvider,
  useFieldMetadata,
} from "@platform/plan-grid-core";
import {
  PlanDesignerProvider,
  usePlanDesignerFeatureFlags,
  usePlanDesignerUI,
} from "@platform/plan-grid-core";

import { RfpData, useListBenefitPlanGroups } from "./api";
import { PlanDialogRenderer } from "./components/PlanEditor/components/PlanDialogRenderer";
import UtilityBar from "./components/UtilityBar";
import { useAiDocsContextMenu } from "./hooks/useAiDocsContextMenu";
import { useBenefitPlanGroupSync } from "./hooks/useBenefitPlanGroupSync";
import { useHandlePlanReorder } from "./hooks/useHandlePlanReorder";
import { usePlanDesignerAutosave } from "./hooks/usePlanDesignerAutosave";
import { usePlanDesignerCoverageSelection } from "./hooks/usePlanDesignerCoverageSelection";
import { usePlanDesignerCoverageState } from "./hooks/usePlanDesignerCoverageState";
import { usePlanDialogHandles } from "./hooks/usePlanDialogHandles";
import {
  usePlanEditorMainMenuItems,
  usePlanLevelMenuItems,
} from "./hooks/usePlanEditorMenuItems";
import { buildDefaultSideBar } from "./util/gridDefaults";
import { hideProposalOnlyRows } from "./util/hideProposalOnlyRows";
import { hyphenateUnknownCommissions } from "./util/hyphenateUnknownCommissions";
import { buildSuppressRateOnlyBenefitsIncluded } from "./util/suppressRateOnlyBenefitsIncluded";

import type {
  CoverageStatus,
  ValidationConfig,
} from "@platform/plan-grid-core";
import type {
  GridApi,
  ICellRendererParams,
  SideBarDef,
} from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import type { ComponentType, JSX, PropsWithChildren, RefObject } from "react";
import type { PlanDocumentMap } from "@platform/plan-grid-core";
import type { ColumnWidthPreset } from "./components/UtilityBar";

import "./PlanDesigner.css";

export interface PlanDesignerProps {
  coverage?: RfpCoverage;
  rfp: RfpData;
  /** Optional configuration to override defaults */
  configuration?: Partial<PlanDesignerConfiguration>;
  /** Callback for back navigation */
  onBack?: VoidFunction;
  /** Callback for plan column reordering */
  onPlanReorder?: (planOrder: string[]) => void;
  /** Validation configuration for benefit fields */
  validationConfig?: ValidationConfig;
  /** Callback when a coverage is selected */
  onCoverageSelect?: (coverageType: CoverageTypeEnum) => void;
  /** Callback when the grid is ready, provides GridAPI for aligned grids */
  onGridReady?: (gridApi: GridApi) => void;
  /**
   * Reports the metrics aligned grids (RatesGrid/SummaryGrid) need to track the
   * PlanEditor grid exactly: rendered plan-column widths (colId → px) applied as
   * explicit widths, and the right gutter (px) occupied by the PlanEditor
   * sidebar, reserved on the aligned grids so columns stay aligned when scrolled
   * to the right edge.
   */
  onAlignmentMetricsChange?: (metrics: {
    columnWidths: Record<string, number>;
    rightGutter: number;
  }) => void;
  /** Refs to other AG Grid instances to align with (e.g., RatesGrid, SummaryGrid) */
  alignedGrids?: RefObject<AgGridReact>[];
  /** Optional AI auto count component to render above the warning banner */
  aiAutoCount?: React.ReactNode;
  /** Optional warning banner component to render above the grid */
  warningBanner?: React.ReactNode;
  /** When true, shows status select UI instead of completion toggle button */
  useCoverageStatusSelect?: boolean;
  tableLegend?: React.ReactNode;
  CellRenderer?: ComponentType<PropsWithChildren<ICellRendererParams>>;
  /** Optional row-data transformer applied by PlanEditor. */
  transformRowData?: (rowData: unknown[]) => unknown[];
  /** Optional map of plan UUID → source document filename for document-level column grouping. */
  planDocumentMap?: PlanDocumentMap;
  /** Optional external save timestamp for saves outside PlanDesigner autosave. */
  lastSavedAt?: Date;
  /** Optional callback fired when PlanDesigner completes a save. */
  onLastSaved?: (savedAt: Date) => void;
  status?: CoverageStatus;
  /** When true, the full screen toggle reflects the active state. */
  isFullScreen?: boolean;
  /** Callback to toggle full screen mode; when provided, the toggle button is shown. */
  onToggleFullScreen?: VoidFunction;
  /** Active plan-column width preset (drives the toggle highlight). */
  columnWidth?: ColumnWidthPreset;
  /** Callback to change the plan-column width preset; when provided, the toggle is shown. */
  onColumnWidthChange?: (preset: ColumnWidthPreset) => void;
  /** Plan-column width as a fraction of default (1 = wide); scales grid column widths. */
  columnWidthScale?: number;
  /** Bump on every preset click to reset all columns to preset sizing (see PlanEditor). */
  columnSizingResetToken?: number;
}

const PlanDesignerContent = forwardRef<
  AgGridReact,
  Omit<PlanDesignerProps, "coverage" | "configuration"> & {
    selectedCoverage: RfpCoverage | undefined;
    getCurrentCoverage: () => RfpCoverage | undefined;
    updateSelectedCoverage: (updatedCoverage?: RfpCoverage) => void;
  }
>(
  (
    {
      selectedCoverage,
      getCurrentCoverage,
      updateSelectedCoverage,
      rfp,
      onBack,
      onPlanReorder,
      validationConfig,
      onCoverageSelect,
      onGridReady,
      onAlignmentMetricsChange,
      alignedGrids = [],
      aiAutoCount,
      warningBanner,
      useCoverageStatusSelect = false,
      tableLegend,
      CellRenderer,
      transformRowData,
      planDocumentMap,
      lastSavedAt,
      onLastSaved,
      status,
      isFullScreen,
      onToggleFullScreen,
      columnWidth,
      onColumnWidthChange,
      columnWidthScale,
      columnSizingResetToken,
    },
    ref,
  ): JSX.Element => {
    const uiConfig = usePlanDesignerUI();
    const { enableNeedsReviewPanel, enableCellValidation } =
      usePlanDesignerFeatureFlags();

    // Capture the grid API locally so the duplicate flow can commit an open
    // cell editor before the server-side copy runs.
    const gridApiRef = useRef<GridApi | null>(null);
    const handleGridReady = useCallback(
      (gridApi: GridApi): void => {
        gridApiRef.current = gridApi;
        onGridReady?.(gridApi);
      },
      [onGridReady],
    );

    // Default to AI Docs context menu when no external menu builder is provided
    const getContextMenuItems = useAiDocsContextMenu(
      useCoverageStatusSelect,
      enableCellValidation,
    );
    const isUtilityBarEnabled = uiConfig.utilityBar?.enabled ?? true; // Default to true for backward compatibility

    // When the Current Policy dropdown is enabled the per-plan Current Carrier
    // field is hidden and superseded by the group-level Current Policy, so it
    // must not block completion (ADR 10.22). Used for both required-field
    // validation and the duplicate-policy completion gate below.
    const currentCarrierPolicyDropdownEnabled = useFeatureIsOn(
      BTX_CURRENT_CARRIER_POLICY_DROPDOWN,
    );

    // Swaps Voluntary Life's three fixed child benefit-description rows for
    // the single flexible childBenefitAmountsByAge field Present already
    // uses (CD-1976 parity). See hideProposalOnlyRows. The save side reads
    // this flag independently in usePlanDesignerAutosave, which is the only
    // place that knows a save is happening.
    const childAgeBandsFieldEnabled = useFeatureIsOn(BTX_CH_BENEFITS_FIELD);

    // Required fields validation for the selected coverage
    const requiredFieldsValidation = useRequiredFieldsValidation(
      selectedCoverage,
      currentCarrierPolicyDropdownEnabled,
      childAgeBandsFieldEnabled,
    );

    // Constants derived from props
    const rfpId = rfp.id;

    const { setBenefitPlanGroups } = useFieldMetadata();

    // Load benefit plan groups for the selected coverage
    const benefitPlanGroupsQuery = useListBenefitPlanGroups(
      {
        rfpId,
        coverageType: selectedCoverage?.coverageType || undefined,
        productType: selectedCoverage?.productType || undefined,
      },
      {
        enabled: !!rfpId && !!selectedCoverage?.type,
      },
    );

    const benefitPlanGroups = benefitPlanGroupsQuery.data;

    // A coverage can't be marked complete while two plan groups share a Current
    // Policy (ADR 10.22). Compute it here to gate the completion control.
    const hasDuplicateCurrentPolicy = useMemo(() => {
      if (!currentCarrierPolicyDropdownEnabled || !benefitPlanGroups) {
        return false;
      }

      const seen = new Set<string>();
      for (const group of benefitPlanGroups) {
        if (!group.currentPolicyUuid) continue;
        if (seen.has(group.currentPolicyUuid)) return true;
        seen.add(group.currentPolicyUuid);
      }

      return false;
    }, [currentCarrierPolicyDropdownEnabled, benefitPlanGroups]);

    const {
      handleAddPlan,
      handleDuplicatePlan,
      handleDeletePlan,
      handleDeletePlanLevel,
      handleNicknamePlan,
      handleAddPlanToGroup,
      handleDuplicatePlanInGroup,
      isDuplicatingPlan,
    } = usePlanDialogHandles(
      rfpId,
      benefitPlanGroups,
      updateSelectedCoverage,
      getCurrentCoverage,
      selectedCoverage,
    );

    const planLevelMenuItems = usePlanLevelMenuItems(
      handleAddPlanToGroup,
      handleDuplicatePlanInGroup,
    );
    // Plan-level menu (Change Option, New Plan, Duplicate Plan, Delete Plan
    // via the 3-dot icon on option headers) is always available, regardless
    // of the AI Docs UI feature flag.
    const getPlanLevelMenuItem = planLevelMenuItems;

    // A single-plan group's top header folds the plan-level actions into its
    // plan-group menu, so users get both from the first row instead of having
    // to find the plan's own header row.
    const getMainMenuItems = usePlanEditorMainMenuItems(
      benefitPlanGroups,
      getCurrentCoverage,
      getPlanLevelMenuItem,
    );

    // Handle coverage changes from PlanEditor (when cells are edited)
    const handleCoverageChange = (coverage: RfpCoverage): void => {
      const updatedCoverage = {
        ...coverage,
        updatedAt: new Date().toISOString(), // Update timestamp for cache comparison
      };
      logger.info(
        "[PlanDesigner] Coverage changed, updating state and triggering autosave",
      );
      logger.info("[PlanDesigner] Updated coverage:", { updatedCoverage });

      updateSelectedCoverage(updatedCoverage);
      triggerSave();
    };

    useBenefitPlanGroupSync({
      benefitPlanGroups,
      getCurrentCoverage,
      setBenefitPlanGroups,
      updateSelectedCoverage,
    });

    const { isSaving, isSavePending, triggerSave, flushSave, unsavedFields } =
      usePlanDesignerAutosave({
        rfp,
        getCurrentCoverage,
        updateSelectedCoverage,
        onLastSaved,
      });

    // Duplicate Plan Group copies the coverage's persisted state server-side.
    // Commit the open ("hard selected") cell editor and flush pending edits
    // first so the copy includes them instead of stale data.
    const handleDuplicatePlanWithFlush = useCallback(
      async (args: DuplicateBenefitPlanGroupArgs): Promise<void> => {
        gridApiRef.current?.stopEditing();
        await flushSave();
        handleDuplicatePlan(args);
      },
      [flushSave, handleDuplicatePlan],
    );

    const { handlePlanReorder, waitForPendingReorders } = useHandlePlanReorder({
      benefitPlanGroups,
      selectedCoverage,
      updateSelectedCoverage,
      onPlanReorder,
      rfpId,
      triggerSave,
    });

    // The export reads the RFP and plan groups from the server, so commit the
    // open cell editor, flush pending saves, and let in-flight plan reorders
    // land first or the spreadsheet misses them. A partial save leaves values
    // on screen the server never stored, so it blocks the export rather than
    // producing a spreadsheet that disagrees with the grid.
    const handleBeforeExport = useCallback(async (): Promise<void> => {
      gridApiRef.current?.stopEditing();
      const [skippedFields] = await Promise.all([
        flushSave(),
        waitForPendingReorders(),
      ]);
      if (skippedFields.length > 0) {
        throw new Error(`Missing required values: ${skippedFields.join(", ")}`);
      }
    }, [flushSave, waitForPendingReorders]);

    const {
      isLoadingCoverage,
      isLoading: isLoadingCoverageQuery,
      handleCoverageSelect,
    } = usePlanDesignerCoverageSelection({
      rfp,
      updateSelectedCoverage,
      onCoverageSelect,
    });

    const getSidebar = useCallback(
      (coverage: RfpCoverage | undefined): SideBarDef | false =>
        buildDefaultSideBar({
          ecoRfpId: rfpId,
          coverageId: coverage?.id,
          benefitPlanType: normalizeNoteBenefitPlanType(coverage?.productType),
          enabledBTXReviewPanel: enableNeedsReviewPanel,
        }),
      [rfpId, enableNeedsReviewPanel],
    );

    // Blanks the Vol Life Benefits Included toggles seeded onto rate-only plans
    // (CD-3082). Observes the final row set, so it runs after the transforms
    // that add or remove rows.
    const suppressRateOnlyBenefitsIncluded = useMemo(
      () => buildSuppressRateOnlyBenefitsIncluded(selectedCoverage),
      [selectedCoverage],
    );

    // Runs the caller's transform first so it still observes the full row set.
    const transformRowDataForBuild = useCallback(
      (rowData: unknown[]): unknown[] =>
        suppressRateOnlyBenefitsIncluded(
          hyphenateUnknownCommissions(
            hideProposalOnlyRows(
              transformRowData ? transformRowData(rowData) : rowData,
              { childAgeBandsFieldEnabled },
            ),
          ),
        ),
      [
        transformRowData,
        childAgeBandsFieldEnabled,
        suppressRateOnlyBenefitsIncluded,
      ],
    );

    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          width: "auto",
          height: "auto",
        }}
      >
        {isUtilityBarEnabled && (
          <Box
            sx={
              isFullScreen
                ? {
                    position: "sticky",
                    top: 0,
                    zIndex: (theme) => theme.zIndex.appBar + 2,
                    bgcolor: "background.default",
                  }
                : undefined
            }
          >
            <UtilityBar
              rfp={rfp}
              rfpId={rfpId}
              onBeforeExport={handleBeforeExport}
              selectedCoverage={selectedCoverage}
              onCoverageSelect={handleCoverageSelect}
              requiredFieldsValidation={requiredFieldsValidation}
              hasDuplicateCurrentPolicy={hasDuplicateCurrentPolicy}
              lastUpdated={getLastUpdatedLabel(
                isLoadingCoverage || isLoadingCoverageQuery,
                isSaving || isSavePending,
                lastSavedAt,
              )}
              unsavedFields={unsavedFields}
              onBack={onBack}
              onFullScreen={onToggleFullScreen}
              isFullScreen={isFullScreen}
              columnWidth={columnWidth}
              onColumnWidthChange={onColumnWidthChange}
              useCoverageStatusSelect={useCoverageStatusSelect}
              isPlanLevelDropdownEnabled={!!planDocumentMap}
              status={status}
            />
          </Box>
        )}
        {aiAutoCount && <Box>{aiAutoCount}</Box>}
        {warningBanner && <Box mt={3}>{warningBanner}</Box>}
        <Box sx={{ flex: 1, height: "auto", minHeight: 0, mt: 3 }}>
          {tableLegend && <Box>{tableLegend}</Box>}
          <ValidationProvider validationConfig={validationConfig}>
            <EditorValuesFilterProvider filter={stripNotOffered}>
              <PlanEditor
                ref={ref}
                key={selectedCoverage?.type}
                coverage={selectedCoverage}
                getCurrentCoverage={getCurrentCoverage}
                onCoverageChange={handleCoverageChange}
                onPlanReorder={handlePlanReorder}
                onAlignmentMetricsChange={onAlignmentMetricsChange}
                onGridReady={handleGridReady}
                alignedGrids={alignedGrids}
                benefitPlanGroups={benefitPlanGroups}
                planDocumentMap={planDocumentMap}
                // BTX-only seam — explicitly true here, never inferred from
                // planDocumentMap (MPX also sets that, for unrelated labels).
                enablePlanLevelDropdown
                CellRenderer={CellRenderer}
                transformRowData={transformRowDataForBuild}
                getContextMenuItems={getContextMenuItems}
                getMainMenuItems={getMainMenuItems}
                getPlanLevelMenuItem={getPlanLevelMenuItem}
                getSidebar={getSidebar}
                isMutating={isDuplicatingPlan}
                columnWidthScale={columnWidthScale}
                columnSizingResetToken={columnSizingResetToken}
              />
            </EditorValuesFilterProvider>
            <PlanDialogRenderer
              onAddPlan={handleAddPlan}
              onNicknamePlan={handleNicknamePlan}
              onDeletePlan={handleDeletePlan}
              onDeletePlanLevel={handleDeletePlanLevel}
              onDuplicatePlan={handleDuplicatePlanWithFlush}
            />
          </ValidationProvider>
        </Box>
      </Box>
    );
  },
);

export const PlanDesigner = forwardRef<AgGridReact, PlanDesignerProps>(
  (
    {
      coverage,
      rfp,
      configuration,
      onBack = () => {}, // for now default to no-op
      onPlanReorder,
      validationConfig,
      onCoverageSelect,
      onGridReady,
      onAlignmentMetricsChange,
      alignedGrids = [],
      aiAutoCount,
      warningBanner,
      useCoverageStatusSelect = false,
      tableLegend,
      CellRenderer,
      transformRowData,
      planDocumentMap,
      lastSavedAt,
      onLastSaved,
      status,
      isFullScreen,
      onToggleFullScreen,
      columnWidth,
      onColumnWidthChange,
      columnWidthScale,
      columnSizingResetToken,
    },
    ref,
  ) => {
    const { selectedCoverage, getCurrentCoverage, updateSelectedCoverage } =
      usePlanDesignerCoverageState(coverage);

    return (
      <PlanDesignerProvider
        configuration={{
          ...configuration,
          ecoRfpId: rfp.id, // Provide ecoRfpId from rfp data
        }}
      >
        <NotificationProvider>
          <DialogProvider<DialogName>>
            <FieldMetadataProvider ecoRfpId={rfp.id}>
              <PlanDesignerContent
                ref={ref}
                rfp={rfp}
                selectedCoverage={selectedCoverage}
                getCurrentCoverage={getCurrentCoverage}
                updateSelectedCoverage={updateSelectedCoverage}
                onBack={onBack}
                onPlanReorder={onPlanReorder}
                validationConfig={validationConfig}
                onCoverageSelect={onCoverageSelect}
                onGridReady={onGridReady}
                onAlignmentMetricsChange={onAlignmentMetricsChange}
                alignedGrids={alignedGrids}
                aiAutoCount={aiAutoCount}
                warningBanner={warningBanner}
                useCoverageStatusSelect={useCoverageStatusSelect}
                tableLegend={tableLegend}
                CellRenderer={CellRenderer}
                transformRowData={transformRowData}
                planDocumentMap={planDocumentMap}
                lastSavedAt={lastSavedAt}
                onLastSaved={onLastSaved}
                status={status}
                isFullScreen={isFullScreen}
                onToggleFullScreen={onToggleFullScreen}
                columnWidth={columnWidth}
                onColumnWidthChange={onColumnWidthChange}
                columnWidthScale={columnWidthScale}
                columnSizingResetToken={columnSizingResetToken}
              />
            </FieldMetadataProvider>
          </DialogProvider>
        </NotificationProvider>
      </PlanDesignerProvider>
    );
  },
);

// Set display name for better debugging
PlanDesignerContent.displayName = "PlanDesignerContent";
PlanDesigner.displayName = "PlanDesigner";
export default PlanDesigner;
